/**
 * FILL_IN_BLANK template markers — indexed SoT `{{0}}`…`{{n-1}}`.
 *
 * Write path (normalize + one-shot backfill): may convert legacy `{{blank}}`
 * via `normalizeBlankTemplateMarkers`, then assert.
 * Player path: `parseFillInBlankTemplate` is indexed-only (fails closed).
 */

const INDEXED_BLANK_RE = /\{\{(\d+)\}\}/g;
const LEGACY_BLANK_REPLACE_RE = /\{\{blank\}\}/gi;
const HAS_LEGACY_BLANK_RE = /\{\{blank\}\}/i;

export type FillInBlankTemplatePart =
	| { type: "text"; value: string }
	| { type: "blank"; index: number };

export class FillInBlankTemplateError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "FillInBlankTemplateError";
	}
}

/** Write/backfill only: sequential `{{blank}}` → `{{0}}`, `{{1}}`, … */
export function normalizeBlankTemplateMarkers(template: string): string {
	let index = 0;
	return template.replace(LEGACY_BLANK_REPLACE_RE, () => `{{${index++}}}`);
}

export function countIndexedBlankMarkers(template: string): number {
	const matches = template.match(INDEXED_BLANK_RE);
	return matches?.length ?? 0;
}

export function listIndexedBlankIndices(template: string): number[] {
	const indices: number[] = [];
	for (const match of template.matchAll(INDEXED_BLANK_RE)) {
		indices.push(Number(match[1]));
	}
	return indices;
}

/**
 * Assert template has exactly one of each `{{0}}`…`{{blankCount-1}}`
 * and no leftover `{{blank}}`.
 */
export function assertIndexedBlankTemplate(
	template: string,
	blankCount: number,
): void {
	if (blankCount < 1) {
		throw new FillInBlankTemplateError(
			`blankCount must be >= 1 (got ${blankCount})`,
		);
	}
	if (HAS_LEGACY_BLANK_RE.test(template)) {
		throw new FillInBlankTemplateError(
			"Template still contains legacy {{blank}} markers; normalize before assert",
		);
	}

	const indices = listIndexedBlankIndices(template);
	if (indices.length !== blankCount) {
		throw new FillInBlankTemplateError(
			`Expected ${blankCount} indexed blank marker(s), found ${indices.length}`,
		);
	}

	const seen = new Set<number>();
	for (const index of indices) {
		if (index < 0 || index >= blankCount) {
			throw new FillInBlankTemplateError(
				`Blank index {{${index}}} out of range for blankCount=${blankCount}`,
			);
		}
		if (seen.has(index)) {
			throw new FillInBlankTemplateError(
				`Duplicate blank index {{${index}}}`,
			);
		}
		seen.add(index);
	}

	for (let expected = 0; expected < blankCount; expected += 1) {
		if (!seen.has(expected)) {
			throw new FillInBlankTemplateError(
				`Missing required blank marker {{${expected}}}`,
			);
		}
	}
}

/**
 * Player parse — indexed-only. Throws FillInBlankTemplateError on
 * legacy markers, gaps, duplicates, or empty blank set.
 */
export function parseFillInBlankTemplate(
	template: string,
): FillInBlankTemplatePart[] {
	if (HAS_LEGACY_BLANK_RE.test(template)) {
		throw new FillInBlankTemplateError(
			"Legacy {{blank}} markers are not supported in the player",
		);
	}

	const indices = listIndexedBlankIndices(template);
	if (indices.length === 0) {
		throw new FillInBlankTemplateError(
			"Template has no indexed blank markers {{0}}…",
		);
	}

	const blankCount = Math.max(...indices) + 1;
	assertIndexedBlankTemplate(template, blankCount);

	const parts: FillInBlankTemplatePart[] = [];
	let lastIndex = 0;
	for (const match of template.matchAll(INDEXED_BLANK_RE)) {
		const start = match.index ?? 0;
		if (start > lastIndex) {
			parts.push({
				type: "text",
				value: template.slice(lastIndex, start),
			});
		}
		parts.push({ type: "blank", index: Number(match[1]) });
		lastIndex = start + match[0].length;
	}
	if (lastIndex < template.length) {
		parts.push({ type: "text", value: template.slice(lastIndex) });
	}
	return parts;
}

/** Safe player parse without throwing. */
export function tryParseFillInBlankTemplate(
	template: string,
):
	| { ok: true; parts: FillInBlankTemplatePart[] }
	| { ok: false; error: string } {
	try {
		return { ok: true, parts: parseFillInBlankTemplate(template) };
	} catch (error) {
		return {
			ok: false,
			error:
				error instanceof Error
					? error.message
					: "Invalid fill-in-blank template",
		};
	}
}
